import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { App as CapacitorApp } from '@capacitor/app';

/**
 * Initialise Capacitor-only concerns at app boot.
 *
 * Web builds get a no-op — nothing changes for the preview URL.
 * Native builds get:
 *   - Dark status bar matching the ValliRider chrome
 *   - Splash auto-hide once React has mounted
 *   - A back-button hardening on Android that closes the app from
 *     the top-level route rather than exiting the WebView.
 *
 * Both web and native ALWAYS get the shake-to-undo neutraliser: the
 * "Undo" alert popped by iOS Safari when the handlebars vibrate is a
 * well-known blocker for real off-road tests. Native builds also get
 * `applicationSupportsShakeToEdit = false` set at the Swift level in
 * AppDelegate — that fully disables the gesture there. On web we rely
 * on aggressive focus stealing (below), which is enough because the
 * Undo alert only appears when a focused input has recent edits.
 */

function installShakeToUndoBlocker() {
  if (typeof window === 'undefined') return;
  // Selective focus stripping: only strip focus from NON-editable
  // elements. Same allow-list already used by useTracking.js's own
  // focus handler — INPUT / TEXTAREA / contentEditable stay focused
  // so the user can actually type into the destination search and
  // the voice transcript boxes. Without this guard, the aggressive
  // blur made the destination field impossible to use.
  const stripFocus = () => {
    try {
      const el = document.activeElement;
      if (!el) return;
      const tag = (el.tagName || '').toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable) return;
      if (typeof el.blur === 'function') el.blur();
    } catch { /* silent */ }
  };
  document.addEventListener('focusin', stripFocus, true);
  // Some Capacitor + Safari builds still surface the Undo alert via
  // window's motion event when the accelerometer spikes. We eat
  // devicemotion at capture-phase to reduce false triggers.
  const swallowMotion = (e) => {
    try { e.stopImmediatePropagation && e.stopImmediatePropagation(); } catch { /* silent */ }
  };
  window.addEventListener('devicemotion', swallowMotion, { capture: true, passive: true });
}

export async function initCapacitor() {
  // Shake-to-undo blocker: runs on BOTH web and native. Users who
  // launch the app from a browser or a home-screen shortcut also
  // suffer from iOS Safari's Undo alert triggered by handlebar
  // vibrations. The editable allow-list above keeps text inputs
  // typable regardless of platform.
  installShakeToUndoBlocker();

  if (!Capacitor.isNativePlatform()) return;

  try {
    await StatusBar.setStyle({ style: Style.Dark });
    // Android only; iOS ignores.
    await StatusBar.setBackgroundColor({ color: '#0A0A0A' });
  } catch { /* silent */ }

  try {
    await SplashScreen.hide({ fadeOutDuration: 300 });
  } catch { /* silent */ }

  try {
    CapacitorApp.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        CapacitorApp.exitApp();
      }
    });
  } catch { /* silent */ }
}
